import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewEncapsulation,
  inject
} from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ValidationErrors,
  Validators
} from '@angular/forms';
import {
  Category,
  Filter,
  FilterOption,
  Product,
  ProductGender,
  ProductLabel
} from '@infrab4a/connect';
import { MessageService } from 'primeng/api';
import {
  AutoCompleteCompleteEvent,
  AutoCompleteUnselectEvent
} from 'primeng/autocomplete';
import { CheckboxChangeEvent } from 'primeng/checkbox';
import { ChipsAddEvent } from 'primeng/chips';
import { lastValueFrom, map } from 'rxjs';
import { UserControllerService } from 'src/app/admin-api/api/api';
import { BaseConnectService } from 'src/app/connect-api/api/shop/base-connect.service';
import { ShopCategoryFilterService } from 'src/app/connect-api/api/shop/shop-category-filter.service';
import { ShopCategoryService } from 'src/app/connect-api/api/shop/shop-category.service';
import { ShopLoggingService } from 'src/app/connect-api/api/shop/shop-log-service';
import { ShopProductGroupService } from 'src/app/connect-api/api/shop/shop-product-group.service';
import {
  ProductCuradoriaFields,
  ShopProductService
} from 'src/app/connect-api/api/shop/shop-product.service';
import { ShopProduct } from 'src/app/connect-api/models/ShopProduct';
import { AppDialogService } from 'src/app/services/dialog.service';
import { LoaderService } from 'src/app/services/loader.service';
import { Supplier, SupplierService } from 'src/app/services/supplier.service';
import { FormUtil } from 'src/app/utils/form.util';
import { slugify } from 'src/app/utils/slug.util';
import { DropdownFilter } from '../table';
import {
  SHOP_PRODUCT_REQUIRED_CONTROL_PATHS,
  getBrandCategoryRequiredError,
  getInvalidShopProductRequiredFields,
  getShopProductPublishError,
  getShopProductSaveError,
  isShopProductRequiredControlInvalid,
  resolveBrandCategoryValue,
  resolveProductGroupId,
  sanitizeShopProductUpdatePayload,
  toShopProductRequiredFields
} from './shop-product-form.util';

@Component({
  selector: 'app-shop-product-form',
  templateUrl: './shop-product-form.component.html',
  styleUrl: './shop-product-form.component.scss',
  encapsulation: ViewEncapsulation.None
})
export class ShopProductFormComponent implements OnInit {
  @Input() product: Partial<ShopProduct>;
  @Input() categories: Array<Category>;
  @Input() permission = true;

  @Output() productUpdated = new EventEmitter();

  private productService = inject(ShopProductService);
  private productGroupService = inject(ShopProductGroupService);
  private categoryService = inject(ShopCategoryService);
  private messageService = inject(MessageService);
  private filterService = inject(ShopCategoryFilterService);
  private shopLoggingService = inject(ShopLoggingService);
  private userService = inject(UserControllerService);
  private supplierService = inject(SupplierService);

  static readonly CURVE_VALUES = ['A+', 'A', 'B', 'C'];

  private brandInCategoryListValidator = (
    control: AbstractControl
  ): ValidationErrors | null => {
    return getBrandCategoryRequiredError(
      this.brands,
      control.value,
      control.touched || control.dirty
    );
  };

  form = this.createFormGroup();
  genders = [
    { name: 'Masculino', value: 'male' },
    { name: 'Feminino', value: 'female' },
    { name: 'Unissex', value: 'unisex' }
  ];
  labels = [
    { name: 'Nenhum', value: 'null' },
    { name: 'Outlet', value: 'outlet' },
    { name: 'Promoção', value: 'on-sale' },
    { name: 'Últimas unidades', value: 'last-units' },
    { name: 'Glamstar', value: 'glamstar' }
  ];
  groups: { name: string; value: number }[] = [];
  validities = [
    { name: 'Sim', value: true },
    { name: 'Não', value: false }
  ];
  categoriesForDropdown: Array<DropdownFilter>;
  chipRegex: any = /\r|\n|;|,/;
  filterOptions: Filter[] = [];
  filterOptionsAvailable: FilterOption[] = [];
  groupedCategories: Array<{
    label: string;
    value: string;
    items: Array<{ label: string; value: string }>;
  }> = [];
  brands: Category[];
  showVariantsOptions = [
    { name: 'Exibir variantes', value: true },
    { name: 'Exibir produto principal', value: false }
  ];
  suppliersOptions: Supplier[] = [];
  curveOptions = ShopProductFormComponent.CURVE_VALUES.map((value) => ({
    name: value,
    value
  }));
  validadeLoading = false;
  private showRequiredFieldHints = false;
  private requiredHintsForPublish = false;

  async ngOnInit(): Promise<void> {
    LoaderService.showLoader();
    this.groupedCategories = this.getCategoryTreeGrouped();

    await Promise.all([
      this.getProductGroups(),
      this.findFiltersOptions(),
      this.getBrandsOptions(),
      this.getSuppliersOptions()
    ]);
    this.categoriesForDropdown = [
      { label: 'Sem categoria', value: BaseConnectService.NULL }
    ].concat(
      this.categories.map((c) => ({
        label: `${c.reference} ${c.name} [${c.shop}]`,
        value: c
      }))
    );
    this.form = this.createFormGroup();

    if (this.product?.category?.id) {
      await this.findCategoryTree(this.product?.category?.id);
    }
    this.recalculatePrices();
    await this.loadCuradoriaFields();

    LoaderService.showLoader(false);

    // Validade comes from Millenium (external, potentially slow) — load it
    // in the background so it doesn't block rendering the rest of the form.
    void this.loadValidade();
  }

  private getCategoryTreeGrouped() {
    const options = [];
    const levelThree = this.categories.filter(
      (category) => category.reference.length >= 5
    );
    const grouped = levelThree.reduce<Record<string, Category[]>>(
      (acc, item) => {
        const prefix = item.reference.slice(0, 3);
        const levelTwo = this.categories.find(
          (category) => category.reference === prefix
        );

        const name = `${prefix} - ${levelTwo?.name?.toString()}`;
        if (!acc[name]) {
          acc[name] = [];
        }

        acc[name].push(item);

        return acc;
      },
      {}
    );

    for (const key in grouped) {
      options.push({
        label: key,
        value: key,
        items: grouped[key]
          .map((option) => {
            return {
              label: `${option.reference} ${option.name} [${option.shop}]`,
              value: option
            };
          })
          .sort((a, b) => {
            return (
              Number(a.value.reference.replaceAll('.', '')) -
              Number(b.value.reference.replaceAll('.', ''))
            );
          })
      });
    }

    return options.sort((a, b) => a.label.localeCompare(b.label));
  }

  async getProductGroups() {
    const response = await this.productGroupService.getList();

    this.groups = response.map((group) => ({
      name: `Grupo: ${group.id} - ${group.name}`,
      value: +group.id
    }));
  }

  async findFiltersOptions(): Promise<void> {
    const filterOptions = await this.filterService.getFilterList();
    filterOptions.sort((f1, f2) => {
      if (
        FormUtil.semAcento(f1.description).toUpperCase() <
        FormUtil.semAcento(f2.description).toUpperCase()
      )
        return -1;
      if (
        FormUtil.semAcento(f1.description).toUpperCase() >
        FormUtil.semAcento(f2.description).toUpperCase()
      )
        return 1;
      return 0;
    });
    this.filterOptions = filterOptions;
  }

  async getBrandsOptions() {
    this.brands = await this.categoryService
      .getCategoriesList({
        fields: ['id', 'name'],
        filters: [
          {
            field: 'brandCategory',
            condition: 'equals',
            value: true,
            fieldType: 'boolean'
          },
          {
            field: 'isWishlist',
            condition: 'equals',
            value: false,
            fieldType: 'boolean'
          }
        ],
        sortBy: 'name',
        sortDirection: 'asc',
        page: 0,
        pageSize: 10000
      })
      .then((res) => res.content || []);
  }

  async getSuppliersOptions(): Promise<void> {
    try {
      this.suppliersOptions = await this.supplierService.listSuppliers();
    } catch (error) {
      this.suppliersOptions = [];
      AppDialogService.showErrorDialog(error);
    }
  }

  private async loadCuradoriaFields(): Promise<void> {
    if (!this.product?.id) return;
    try {
      const curadoria = await this.productService.getCuradoriaFields(
        this.product.id
      );
      this.form.controls['caixariaMinima'].setValue(curadoria.caixariaMinima, {
        emitEvent: false
      });
      this.form.controls['suppliers'].setValue(
        this.reconcileSuppliers(curadoria.suppliers),
        { emitEvent: false }
      );
      this.form.controls['curve'].setValue(curadoria.curve, {
        emitEvent: false
      });
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    }
  }

  private reconcileSuppliers(suppliers: Supplier[]): Supplier[] {
    if (!suppliers?.length) return [];
    return suppliers.map(
      (supplier) =>
        this.suppliersOptions.find((o) => o.clientId === supplier.clientId) ??
        supplier
    );
  }

  private async loadValidade(): Promise<void> {
    if (!this.product?.id) return;
    this.validadeLoading = true;
    this.form.controls['validade'].setValue('Carregando...', {
      emitEvent: false
    });
    this.form.controls['estoqueLote'].setValue('Carregando...', {
      emitEvent: false
    });
    try {
      const result = await this.productService.getMilleniumExpiration({
        productId: this.product.id,
        sku: this.product.sku
      });
      // stock === null means the ERP lookup failed (Millenium is flaky and can
      // answer HTTP 400 intermittently) — it must NOT be shown as "no stock".
      // stock === 0 is a real, successful "out of stock" answer.
      let validade: string;
      let estoqueLote: string;
      if (result.stock == null) {
        validade = 'Não foi possível consultar o estoque';
        estoqueLote = 'Indisponível';
      } else if (result.stock > 0) {
        validade = this.formatValidade(result.expirationDate);
        // lotStock is the balance of the specific lot shown with this validade,
        // not the total across lots.
        estoqueLote =
          result.lotStock != null
            ? `${result.lotStock} un.`
            : 'Sem estoque no lote';
      } else {
        validade = 'Produto sem estoque';
        estoqueLote = '0 un.';
      }
      this.form.controls['validade'].setValue(validade, { emitEvent: false });
      this.form.controls['estoqueLote'].setValue(estoqueLote, {
        emitEvent: false
      });
    } catch {
      this.form.controls['validade'].setValue('Indisponível', {
        emitEvent: false
      });
      this.form.controls['estoqueLote'].setValue('Indisponível', {
        emitEvent: false
      });
    } finally {
      this.validadeLoading = false;
    }
  }

  private formatValidade(date: string | null): string {
    if (!date) return 'Sem validade cadastrada';
    // The API returns a date-only ISO string (yyyy-MM-dd). Parsing it with
    // `new Date(iso)` treats it as UTC midnight, which renders one day earlier
    // in UTC-3. Build the date from its parts so it stays in local time.
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(date);
    if (!match) return date;
    const [, year, month, day] = match;
    const parsed = new Date(Number(year), Number(month) - 1, Number(day));
    return isNaN(parsed.getTime()) ? date : parsed.toLocaleDateString('pt-BR');
  }

  private curveValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (value === null || value === undefined || value === '') return null;
    return ShopProductFormComponent.CURVE_VALUES.includes(value)
      ? null
      : { invalidCurve: true };
  }

  private extractCuradoriaChanges(
    product: Record<string, unknown>
  ): Partial<ProductCuradoriaFields> | null {
    const changes: Partial<ProductCuradoriaFields> = {};
    if ('caixariaMinima' in product)
      changes.caixariaMinima = (product['caixariaMinima'] as number) ?? null;
    if ('suppliers' in product)
      changes.suppliers = (product['suppliers'] as Supplier[]) ?? [];
    if ('curve' in product)
      changes.curve = (product['curve'] as string) ?? null;

    delete product['caixariaMinima'];
    delete product['suppliers'];
    delete product['curve'];
    delete product['validade'];
    delete product['estoqueLote'];

    return Object.keys(changes).length ? changes : null;
  }

  async findCategoryTree(categoryId?: string): Promise<void> {
    try {
      let tree = 'Sem categoria';
      if (categoryId) {
        const categories =
          await this.categoryService.getCategoriesTree(categoryId);
        tree = categories
          .reverse()
          .map((c) => `${c.reference} ${c.name}`)
          .toString()
          .replaceAll(',', ' > ');
      }
      this.form.controls['categoryTree'].setValue(tree, { emitEvent: false });
      this.form.controls['categoryTree'].markAsPristine();
      this.form.controls['categoryTree'].markAsUntouched();
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    }
  }

  onPublishedChange(event: CheckboxChangeEvent): void {
    if (!event.checked) return;

    const invalidFields = getInvalidShopProductRequiredFields(
      this.requiredFieldsSnapshot(),
      { forPublish: true, hasVariants: !!this.product?.hasVariants }
    );
    if (!invalidFields.length) return;

    this.form.controls.published.setValue(false, { emitEvent: false });
    this.revealInvalidControls(true);
    AppDialogService.showErrorDialog({
      message: getShopProductPublishError(invalidFields)
    });
  }

  invalidControl(path: string): boolean {
    const control = this.form.get(path);
    if (control?.invalid && (control.dirty || control.touched)) {
      return true;
    }
    if (!this.showRequiredFieldHints) {
      return false;
    }
    return isShopProductRequiredControlInvalid(
      path,
      getInvalidShopProductRequiredFields(this.requiredFieldsSnapshot(), {
        forPublish: this.requiredHintsForPublish,
        hasVariants: !!this.product?.hasVariants
      })
    );
  }

  async submit(): Promise<void> {
    const saveError = this.getPublishSaveError(
      !!this.form.getRawValue().published
    );
    if (saveError) {
      this.revealInvalidControls(!!this.form.getRawValue().published);
      AppDialogService.showErrorDialog({ message: saveError });
      return;
    }

    try {
      LoaderService.showLoader();
      const product = sanitizeShopProductUpdatePayload(
        FormUtil.getTouchedAndDirtyOnly<Partial<Product>>(this.form) as Record<
          string,
          unknown
        >
      ) as Partial<Product>;

      // Curadoria fields (caixaria_minima, suppliers, curve) are not part of
      // the @infrab4a/connect Product model — persist them separately via a
      // dedicated Hasura mutation, and remove them from the standard payload.
      const curadoria = this.extractCuradoriaChanges(
        product as Record<string, unknown>
      );

      let changed = false;

      if (product.name) {
        product.slug = slugify(product.name);
      }

      if (Object.keys(product).length) {
        await this.productService.updateProduct({
          ...product,
          id: this.product.id,
          filters: this.form.value.filters?.map((f) => f.id) || [],
          daysOfUse: product.daysOfUse
            ? parseInt(product.daysOfUse as any)
            : BaseConnectService.NULL
        });

        await this.createLog({
          ...product,
          id: this.product.id,
          filters: this.form.value.filters?.map((f) => f.id) || [],
          daysOfUse: product.daysOfUse
            ? parseInt(product.daysOfUse as any)
            : BaseConnectService.NULL
        });

        if (this.product.hasVariants) {
          for (const variant of this.product.variants) {
            await this.productService.updateVariant(
              {
                brand: product.brand,
                category: product.category
              },
              variant.id
            );
          }
        }

        changed = true;
      }

      if (curadoria) {
        await this.productService.updateCuradoriaFields(
          this.product.id,
          curadoria
        );
        changed = true;
      }

      if (changed) {
        this.productUpdated.emit();
      }
      this.messageService.add({
        severity: 'success',
        detail: 'Produto atualizado com sucesso.',
        summary: 'Sucesso'
      });
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    } finally {
      LoaderService.showLoader(false);
    }
  }

  private requiredFieldsSnapshot() {
    const formValue = this.form.getRawValue();
    return toShopProductRequiredFields({
      ...formValue,
      brand: resolveBrandCategoryValue(this.brands, formValue.brand),
      productDescription: this.product?.description?.description,
      images: this.product?.images ?? formValue.images,
      category: formValue.category,
      categoryId: formValue.category?.id,
      groupId: formValue.groupId,
      validity: formValue.validity,
      type: formValue.type,
      EAN: formValue.EAN,
      NCM: formValue.NCM
    });
  }

  private getPublishSaveError(published: boolean): string | null {
    return getShopProductSaveError(
      published,
      getInvalidShopProductRequiredFields(this.requiredFieldsSnapshot(), {
        forPublish: published,
        hasVariants: !!this.product?.hasVariants
      }),
      this.form.invalid
    );
  }

  private revealInvalidControls(forPublish: boolean): void {
    this.showRequiredFieldHints = true;
    this.requiredHintsForPublish = forPublish;
    const invalidFields = getInvalidShopProductRequiredFields(
      this.requiredFieldsSnapshot(),
      {
        forPublish,
        hasVariants: !!this.product?.hasVariants
      }
    );

    for (const field of invalidFields) {
      const path = SHOP_PRODUCT_REQUIRED_CONTROL_PATHS[field];
      if (!path) continue;
      this.markControlTouchedAndDirty(this.form.get(path));
    }

    this.markInvalidControls(this.form);
  }

  private markInvalidControls(group: FormGroup): void {
    Object.values(group.controls).forEach((control) => {
      if (control instanceof FormGroup) {
        this.markInvalidControls(control);
        return;
      }
      if (control.disabled || control.valid) return;
      this.markControlTouchedAndDirty(control);
    });
  }

  private markControlTouchedAndDirty(control: AbstractControl | null): void {
    if (!control || control.disabled) return;
    control.markAsTouched();
    control.markAsDirty();
    control.updateValueAndValidity({ emitEvent: false });
  }

  recalculatePrices(): void {
    this.form.controls['price'].controls['subscriberPrice'].setValue(
      (1 - this.form.value.price.subscriberDiscountPercentage / 100) *
        (this.form.value.price.price || 0)
    );
    this.form.controls['price'].controls[
      'fullPriceDiscountPercentage'
    ].setValue(
      (100 *
        ((this.form.value.price.fullPrice || 0) -
          (this.form.getRawValue().price.subscriberPrice || 0))) /
        (this.form.value.price.fullPrice || 1)
    );
  }

  addCollectionTag(event?: ChipsAddEvent) {
    if (event?.value) {
      const index = this.form.value.tagsCollection.findIndex(
        (t) => t === slugify((event.value as string).toLowerCase())
      );
      if (index >= 0 && index < this.form.value.tagsCollection.length - 1) {
        this.messageService.add({
          severity: 'warn',
          summary: 'Atenção',
          detail: `Tag já vinculada '${this.form.value.tagsCollection[index]}'`
        });
        this.form.controls['tagsCollection'].setValue(
          this.form.value.tagsCollection.slice(
            0,
            this.form.value.tagsCollection.length - 1
          )
        );
      }
    }
    this.form.controls['tagsCollection'].setValue(
      this.form.value.tagsCollection.map((t) => slugify(t.toLowerCase()))
    );
  }

  addProfileTag(event?: ChipsAddEvent) {
    if (event?.value) {
      const index = this.form.value.tagsProfile.findIndex(
        (t) => t === slugify((event.value as string).toLowerCase())
      );
      if (index >= 0 && index < this.form.value.tagsProfile.length - 1) {
        this.messageService.add({
          severity: 'warn',
          summary: 'Atenção',
          detail: `Tag já vinculada '${this.form.value.tagsProfile[index]}'`
        });
        this.form.controls['tagsProfile'].setValue(
          this.form.value.tagsProfile.slice(
            0,
            this.form.value.tagsProfile.length - 1
          )
        );
      }
    }
    this.form.controls['tagsProfile'].setValue(
      this.form.value.tagsProfile.map((t) => slugify(t.toLowerCase()))
    );
  }

  addSupplyTag(event?: ChipsAddEvent) {
    if (event?.value) {
      const index = this.form.value.tagsSupply.findIndex(
        (t) => t === slugify((event.value as string).toLowerCase())
      );
      if (index >= 0 && index < this.form.value.tagsSupply.length - 1) {
        this.messageService.add({
          severity: 'warn',
          summary: 'Atenção',
          detail: `Tag já vinculada '${this.form.value.tagsSupply[index]}'`
        });
        this.form.controls['tagsSupply'].setValue(
          this.form.value.tagsSupply.slice(
            0,
            this.form.value.tagsSupply.length - 1
          )
        );
      }
    }
    this.form.controls['tagsSupply'].setValue(
      this.form.value.tagsSupply.map((t) => slugify(t.toLowerCase()))
    );
  }

  searchFilters(event: AutoCompleteCompleteEvent): void {
    this.filterOptionsAvailable =
      this.filterTags?.filter(
        (f) =>
          !this.form.value.filters?.some((cf) => cf.id === f.id) &&
          f.id.includes(slugify(FormUtil.semAcento(event.query.toLowerCase())))
      ) || [];
  }

  removeFilter(event: AutoCompleteUnselectEvent): void {
    if (event.value) {
      this.form.controls['filters'].setValue(
        this.form.value.filters.filter((f) => f !== event.value)
      );
    }
  }

  get filterTags(): FilterOption[] {
    return this.filterOptions?.reduce(
      (options, f) => (options = options.concat(f.options)),
      []
    );
  }

  createFormGroup() {
    return new FormGroup({
      name: new FormControl<string>(
        { value: this.product?.name, disabled: !this.permission },
        Validators.required
      ),
      slug: new FormControl<string>(
        { value: this.product?.slug, disabled: !this.permission },
        Validators.required
      ),
      price: new FormGroup({
        price: new FormControl<number>(
          { value: this.product?.price?.price, disabled: !this.permission },
          [Validators.required, Validators.min(0.01)]
        ),
        fullPrice: new FormControl<number>(
          { value: this.product?.price?.fullPrice, disabled: !this.permission },
          [Validators.required, Validators.min(0.01)]
        ),
        subscriberDiscountPercentage: new FormControl<number>(
          {
            value: this.product?.price?.subscriberDiscountPercentage,
            disabled: !this.permission
          },
          [Validators.required, Validators.min(0), Validators.max(100)]
        ),
        fullPriceDiscountPercentage: new FormControl<number>({
          value: this.product?.price?.fullPriceDiscountPercentage,
          disabled: true
        }),
        subscriberPrice: new FormControl<number>({
          value: this.product?.price?.subscriberPrice,
          disabled: true
        }),
        costPrice: new FormControl<number>({
          value: this.product?.costPrice,
          disabled: true
        })
      }),
      hasVariants: new FormControl<boolean>({
        value: this.product?.hasVariants,
        disabled: !this.permission
      }),
      NCM: new FormControl<string>({
        value: this.product?.NCM,
        disabled: !this.permission
      }),
      EAN: new FormControl<string>({
        value: this.product?.EAN,
        disabled: true
      }),
      CEST: new FormControl<string>({
        value: this.product?.CEST,
        disabled: !this.permission
      }),
      weight: new FormControl<number>(
        {
          value: this.product?.weight,
          disabled: !this.permission
        },
        [Validators.required, Validators.min(0.01)]
      ),
      costPrice: new FormControl<number>({
        value: this.product?.costPrice,
        disabled: true
      }),
      images: new FormControl<string[]>({
        value: this.product?.images,
        disabled: !this.permission
      }),
      published: new FormControl<boolean>({
        value: this.product?.published,
        disabled: !this.permission
      }),
      brand: new FormControl<string>(
        {
          value: resolveBrandCategoryValue(this.brands, this.product?.brand),
          disabled: !this.permission
        },
        [this.brandInCategoryListValidator]
      ),
      type: new FormControl<string>({
        value: this.product?.type,
        disabled: !this.permission
      }),
      category: new FormControl<Category>({
        value:
          this.product && this.product.category?.id
            ? this.categories?.find(
                (c) => c.id === this.product.category.id.toString()
              )
            : BaseConnectService.NULL,
        disabled: !this.permission
      }),
      categoryTree: new FormControl<string>({
        value: 'Sem categoria',
        disabled: true
      }),
      video: new FormControl<string>(
        {
          value: this.product?.video,
          disabled: !this.permission
        },
        Validators.pattern(
          /(https?:\/\/)?(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)/
        )
      ),
      gender: new FormControl<ProductGender>(
        {
          value: this.product?.gender,
          disabled: !this.permission
        },
        Validators.required
      ),
      metadata: new FormGroup({
        title: new FormControl<string | undefined>(
          {
            value: this.product?.metadata?.title,
            disabled: !this.permission
          },
          [Validators.required, Validators.minLength(1)]
        ),
        description: new FormControl<string>(
          {
            value: this.product?.metadata?.description,
            disabled: !this.permission
          },
          [Validators.required, Validators.minLength(1)]
        )
      }),
      tagsCollection: new FormControl<string[]>({
        value: this.product?.tagsCollection,
        disabled: !this.permission || this.product?.hasVariants
      }),
      tagsProfile: new FormControl<string[]>({
        value: this.product?.tagsProfile,
        disabled: !this.permission || this.product?.hasVariants
      }),
      tagsSupply: new FormControl<string[]>({
        value: this.product?.tagsSupply,
        disabled: !this.permission || this.product?.hasVariants
      }),
      filters: new FormControl<FilterOption[]>({
        value:
          this.product && this.product.filters
            ? this.product.filters?.map((f) =>
                this.filterTags?.find((ft) => ft.id === f)
              )
            : [],
        disabled: !this.permission
      }),
      label: new FormControl<ProductLabel>({
        value: this.product?.label,
        disabled: !this.permission
      }),
      groupId: new FormControl<number>({
        value: resolveProductGroupId(this.groups, this.product?.groupId),
        disabled: !this.permission
      }),
      validity: new FormControl<boolean>({
        value: this.product?.validity,
        disabled: !this.permission
      }),
      daysOfUse: new FormControl<string>({
        value: this.product?.daysOfUse?.toString(),
        disabled: !this.permission
      }),
      showVariants: new FormControl<boolean>({
        value: this.product?.showVariants,
        disabled: !this.permission || !this.product?.hasVariants
      }),
      caixariaMinima: new FormControl<number>(
        { value: null, disabled: !this.permission },
        [Validators.min(0)]
      ),
      suppliers: new FormControl<Supplier[]>({
        value: [],
        disabled: !this.permission
      }),
      curve: new FormControl<string>(
        { value: null, disabled: !this.permission },
        [this.curveValidator]
      ),
      validade: new FormControl<string>({ value: null, disabled: true }),
      estoqueLote: new FormControl<string>({ value: null, disabled: true })
    });
  }

  slugify(): void {
    const slugControl = this.form.controls['slug'];
    slugControl.setValue(slugify(this.form.value.name.trim()));
    slugControl.markAsTouched();
  }

  async createLog(document: Partial<Product>) {
    const user = await lastValueFrom(
      this.userService.getInfo().pipe(map((data) => data.result))
    );

    return this.shopLoggingService.createLog('products', document, {
      id: user.userId.toString(),
      name: user.displayName,
      email: user.username
    });
  }
}
